/**
 * Viewport queries.
 *
 * One breakpoint decides the shape of the shell: above it the rail, the list
 * and the record pane are all on screen at once, below it they are three
 * surfaces that take turns. 900px is the same line the marketing site draws,
 * and it is the width at which the rail (232px) plus the record pane (400px)
 * stop leaving a usable grid between them.
 *
 * `matchMedia` rather than a measured element: the answer only changes at one
 * width, and a resize observer would run layout work while 900 rows scroll.
 */

import { useCallback, useSyncExternalStore } from 'react';

export const MOBILE_BREAKPOINT = 900;

/** Below this the shell is in its phone shape. `.98` so 900px itself is desktop. */
export const MOBILE_QUERY = `(max-width: ${MOBILE_BREAKPOINT - 0.02}px)`;

/**
 * Subscribe to a media query.
 *
 * The server snapshot is `false`, so the first paint is always the desktop
 * shape — the shell is client-rendered, so the real answer arrives in the same
 * frame and nothing flashes.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {};
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    [query],
  );
  const get = useCallback(
    () =>
      typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia(query).matches
        : false,
    [query],
  );
  return useSyncExternalStore(subscribe, get, () => false);
}

/**
 * Is the shell in its phone shape?
 *
 * Width only, deliberately. A touch laptop is not a phone, and a desktop
 * browser narrowed to 700px is — every layout decision downstream of this is
 * about how many pixels there are, not what is pointing at them. Hit-target
 * sizing is the one thing that IS about the pointer, and that lives in
 * `mobile.css` under `(pointer: coarse)`.
 */
export function useIsMobile(): boolean {
  return useMediaQuery(MOBILE_QUERY);
}
